import { onCleanup } from 'solid-js'
import { isServer } from 'solid-js/web'
import type { RuntimeObject, Box, Dimensions, Position } from '../types'

// ── Colour ────────────────────────────────────────────────────────────────────

// Returns true if a CSS color value represents "no color".
export function isColorNone(v: string | null | undefined): boolean {
	return !v || v === 'none' || v === 'rgba(0, 0, 0, 0)'
}

// Convert a CSS rgb/rgba() string to a hex colour.
// Returns null for transparent / none values, the original string for
// non-rgb values (e.g. named colours), and a 6- or 8-char hex otherwise.
export function rgbToHex(rgb: string | null | undefined): string | null {
	if (!rgb || rgb === 'none') return null
	const m = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/)
	if (!m) return rgb
	const a = m[4] !== undefined ? Math.round(parseFloat(m[4]) * 255) : 255
	if (a === 0) return null
	const hex = '#' + [m[1], m[2], m[3]].map(n => (+n).toString(16).padStart(2, '0')).join('')
	return a < 255 ? hex + a.toString(16).padStart(2, '0') : hex
}

// ── Typography ────────────────────────────────────────────────────────────────

// Extract the first font family name from a CSS font-family string.
// Strips surrounding quotes and ignores fallback fonts.
// e.g. '"Roboto", sans-serif' → 'Roboto'
export function parseFontFamily(cssFamily: string): string {
	return cssFamily.split(',')[0].trim().replace(/^['"]|['"]$/g, '')
}

// ── DOM event helpers ─────────────────────────────────────────────────────────

// Register a document event listener that is automatically removed on cleanup.
// Must be called within a SolidJS reactive context (onMount, createEffect, etc.).
// Called from object setup, which also runs in the SSR pass — where there is no document and no
// event will ever arrive, so the registration is simply dropped. (The read-only poster does render
// on the server, so this can't be left to the client-only gate around the builder.)
export function onDocumentEvent(event: string, handler: EventListener): void {
	if (isServer) return
	document.addEventListener(event, handler)
	onCleanup(() => document.removeEventListener(event, handler))
}

// The poster SVG, and the editor UI that acts on the current selection (toolbar, popups,
// the text/field edit overlays), carry a data-keeps-selection attribute. A mousedown
// anywhere else on the page drops the selection — see InputHandler.
export function keepsSelection(target: EventTarget | null): boolean {
	return target instanceof Element && !!target.closest('[data-keeps-selection]')
}

// Run `fn` whenever the document's web fonts finish loading. Text geometry is measured against
// canvas metrics that fall back to a system font until the real (Google) font arrives, so a box
// fitted on first paint can hug the wrong (fallback-font) layout; this is the seam to re-fit once
// the font is ready. Cleanup-bound to the caller's reactive owner, like onDocumentEvent. (The
// rendered text itself re-wraps reactively via the font epoch read in measureWidth.)
export function onFontsLoaded(fn: () => void): void {
	if (typeof document === 'undefined' || !document.fonts) return
	const handler = () => fn()
	document.fonts.addEventListener('loadingdone', handler)
	onCleanup(() => document.fonts.removeEventListener('loadingdone', handler))
}

// ── Render timing ───────────────────────────────────────────────────────────
//
// Run `fn` after the next paint frame — i.e. once SolidJS has flushed the
// reactive DOM update driven by a just-changed signal/store value and the
// browser has laid the result out. This is the seam to use before measuring
// (getBBox / getBoundingClientRect) or re-fitting text: measuring synchronously
// would read the stale, pre-update DOM.
//
// Some flows need two frames (e.g. frame 1 applies a new width/height, frame 2
// reads the offset that only settles after the re-render) — nest two calls.
// Nothing is painted or measurable in the SSR pass, so the callback is dropped there for the same
// reason as onDocumentEvent's registration.
export function afterRender(fn: () => void): void {
	if (isServer) return
	requestAnimationFrame(fn)
}

// ── Resize ────────────────────────────────────────────────────────────────────

export interface ResizeBounds { max: Dimensions; min?: Dimensions }

const MIN_RESIZE: Dimensions = { width: 30, height: 20 }

// Apply a resize delta for a given handle string ('n','s','e','w' or combinations).
// start: the box at the moment the drag began
// dx/dy: cumulative delta from beginResize
// bounds: { max, min = { width: 30, height: 20 } }
// Returns the new box.
// layoutOwned: the element is a flex-laid-out child whose position the container owns and
// re-places as it resizes. In that case the size may grow to the full container extent
// regardless of the child's current offset — clamping to `max.width - left` (the pinned-edge
// rule below) would stop short for centred/right/bottom alignment — and the position is
// left unchanged because the layout recomputes it from the new size.
export function applyResizeDelta(
	handle: string,
	{ pos: { x: sl, y: st }, dims: { width: sw, height: sh } }: Box,
	dx: number,
	dy: number,
	{ max, min = MIN_RESIZE }: ResizeBounds,
	layoutOwned = false
): Box {
	const { width: maxW, height: maxH } = max
	const { width: minW, height: minH } = min
	let l = sl, t = st, w = sw, h = sh
	if (layoutOwned) {
		if (handle.includes('e')) w = Math.max(minW, Math.min(sw + dx, maxW))
		if (handle.includes('w')) w = Math.max(minW, Math.min(sw - dx, maxW))
		if (handle.includes('s')) h = Math.max(minH, Math.min(sh + dy, maxH))
		if (handle.includes('n')) h = Math.max(minH, Math.min(sh - dy, maxH))
		return { pos: { x: l, y: t }, dims: { width: w, height: h } }
	}
	if (handle.includes('e')) w = Math.max(minW, Math.min(sw + dx, maxW - sl))
	if (handle.includes('s')) h = Math.max(minH, Math.min(sh + dy, maxH - st))
	if (handle.includes('w')) { w = Math.max(minW, Math.min(sw - dx, sl + sw)); l = sl + sw - w }
	if (handle.includes('n')) { h = Math.max(minH, Math.min(sh - dy, st + sh)); t = st + sh - h }
	return { pos: { x: l, y: t }, dims: { width: w, height: h } }
}

export function isCornerHandle(handle: string): boolean {
	return handle.length == 2
}

// Corner resize that keeps the start box's aspect ratio. The cursor delta is projected onto the
// box diagonal, so dragging either axis scales both, and the height bounds become width bounds
// through the ratio so the aspect-locked box still stays on canvas.
export function applyAspectResizeDelta(
	handle: string,
	start: Box,
	dx: number,
	dy: number,
	bounds: ResizeBounds
): Box {
	const { pos: { x: sl, y: st }, dims: { width: sw, height: sh } } = start
	const { max, min = MIN_RESIZE } = bounds
	const { width: maxW, height: maxH } = max
	const { width: minW, height: minH } = min
	if (sw <= 0 || sh <= 0)
		return applyResizeDelta(handle, start, dx, dy, bounds)

	const ratio  = sw / sh
	const wDelta = handle.includes('w') ? -dx : dx
	const hDelta = handle.includes('n') ? -dy : dy
	const scale  = 1 + (wDelta * sw + hDelta * sh) / (sw * sw + sh * sh)

	const lowW   = Math.max(minW, minH * ratio)
	const highW  = Math.min(
		handle.includes('w') ? sl + sw : maxW - sl,
		(handle.includes('n') ? st + sh : maxH - st) * ratio,
	)
	const w = Math.min(Math.max(sw * scale, lowW), Math.max(highW, lowW))
	const h = w / ratio
	const l = handle.includes('w') ? sl + sw - w : sl
	const t = handle.includes('n') ? st + sh - h : st
	return { pos: { x: l, y: t }, dims: { width: w, height: h } }
}

// ── Object tree ───────────────────────────────────────────────────────────────

// Depth-first walk over a nested object tree (objects[].objects[]).
// Calls visitor(obj) for each node. If visitor returns true, traversal stops
// early and walkObjects returns true (useful for find-style searches).
export function walkObjects(arr: RuntimeObject[], visitor: (obj: RuntimeObject) => boolean | void): boolean {
	for (const obj of arr) {
		if (visitor(obj)) return true
		if (obj.type === 'container' && obj.objects && walkObjects(obj.objects, visitor)) return true
	}
	return false
}

// Legacy poster documents stored an object's position as { left, top }; the schema now uses the
// canonical { x, y }. Rewrite any legacy positions in place (recursively) so an old design loads at
// the right coordinates. Idempotent — a design already on { x, y } is left untouched.
type LegacyPos = { left?: number; top?: number; x?: number; y?: number }

// A stored coordinate that is present but not a number — NaN survives a round trip through BSON as a
// double, and JSON.stringify prints it as `null`, so it hides in a design document. Every reader here
// defaults with `??`, which passes NaN straight through, and it then spreads: one NaN box height makes
// its container's whole flex column NaN, and SVG drops an invalid transform, stacking every child at
// the parent's origin. Treated exactly like a missing value, so the type's normal fallback applies.
function isPoisoned(v: number | undefined): boolean {
	return v !== undefined && !Number.isFinite(v)
}

type SizeLike = { width?: number; height?: number }

export function needsGeometryMigration(objs: RuntimeObject[]): boolean {
	return walkObjects(objs, obj => {
		const p = obj.position as LegacyPos | undefined
		const s = (obj as { size?: SizeLike }).size
		return (!!p && (p.left !== undefined || p.top !== undefined))
			|| (!!p && (isPoisoned(p.x) || isPoisoned(p.y)))
			|| (!!s && (isPoisoned(s.width) || isPoisoned(s.height)))
	})
}

export function migrateGeometry(objs: RuntimeObject[]): void {
	walkObjects(objs, obj => {
		const p = obj.position as LegacyPos | undefined
		if (p && (p.left !== undefined || p.top !== undefined))
			obj.position = { x: p.x ?? p.left ?? 0, y: p.y ?? p.top ?? 0 }

		// A poisoned axis falls back to 0 — position is required, so it cannot simply be dropped.
		const pos = obj.position as LegacyPos | undefined
		if (pos && (isPoisoned(pos.x) || isPoisoned(pos.y)))
			obj.position = { x: isPoisoned(pos.x) ? 0 : pos.x ?? 0, y: isPoisoned(pos.y) ? 0 : pos.y ?? 0 }

		// A poisoned size axis is deleted rather than zeroed: size is optional, and its absence is what
		// makes a box fall back to its designed/measured default instead of collapsing to nothing.
		const size = (obj as { size?: SizeLike }).size
		if (size) {
			if (isPoisoned(size.width))  delete size.width
			if (isPoisoned(size.height)) delete size.height
		}
	})
}

// Fills in a stable id for any object missing one, preserving ids the design already
// carries (they persist to the DB and are threaded through flattening — see importExport /
// flatten). Idempotent and collision-safe: minted ids skip any already in use, so calling
// this on a fully-id'd design is a no-op, and on a partially-id'd one it never clashes.
export function assignIds(objs: RuntimeObject[]): void {
	const used = new Set<string>()
	walkObjects(objs, obj => { if (obj.id) used.add(obj.id) })
	let n = 0
	function mint(type: string): string {
		let id: string
		do { id = `${type}-${++n}` } while (used.has(id))
		used.add(id)
		return id
	}
	function walk(list: RuntimeObject[]) {
		for (const obj of list) {
			if (!obj.id) obj.id = mint(obj.type)
			if (obj.type === 'container' && obj.objects)
				walk(obj.objects)
		}
	}
	walk(objs)
}

export function findObjectById(arr: RuntimeObject[], id: string): RuntimeObject | null {
	let found: RuntimeObject | null = null
	walkObjects(arr, obj => { if (obj.id === id) { found = obj; return true } })
	return found
}

// Returns the array that directly contains `id` — either the top-level array or
// a container's objects[]. Used to reorder an object among its siblings.
export function findSiblingArray(arr: RuntimeObject[], id: string): RuntimeObject[] | null {
	if (arr.some(o => o.id === id)) return arr
	for (const o of arr) {
		if (o.type === 'container' && o.objects) {
			const found = findSiblingArray(o.objects, id)
			if (found) return found
		}
	}
	return null
}

export function findParentOf(id: string, arr: RuntimeObject[]): string | null {
	let parentId: string | null = null
	walkObjects(arr, obj => {
		if (obj.type === 'container' && obj.objects?.some(o => o.id === id)) { parentId = obj.id; return true }
	})
	return parentId
}

export function rectsOverlap(a: Box, b: Box): boolean {
	return a.pos.x < b.pos.x + b.dims.width && a.pos.x + a.dims.width > b.pos.x &&
		   a.pos.y < b.pos.y + b.dims.height && a.pos.y + a.dims.height > b.pos.y
}

// Returns IDs of objects in depth-first render order (last = topmost z-layer).
export function getFlatRenderOrder(objects: RuntimeObject[]): string[] {
	const order: string[] = []
	walkObjects(objects, obj => { order.push(obj.id) })
	return order
}

// Convert screen (clientX/Y) coordinates to SVG user-unit coordinates.
export function screenToSVG(svg: SVGSVGElement | null, client: Position): Position {
	if (!svg) return { x: client.x, y: client.y }
	const pt = svg.createSVGPoint()
	pt.x = client.x
	pt.y = client.y
	return pt.matrixTransform(svg.getScreenCTM()!.inverse())
}
